declare const graphQLDocumentTypes: unique symbol
declare const graphQLSelectionData: unique symbol
const graphQLSelectionMarker = Symbol("florisoft.graphql-selection")

export type NoGraphQLVariables = Record<never, never>

/**
 * Static GraphQL selection-set source with the compile-time shape selected by
 * that source. Selections are the only values that may be interpolated into
 * other GraphQL selections/documents.
 */
export interface GraphQLSelection<TData = unknown> {
  readonly source: string
  readonly [graphQLSelectionMarker]: true
  readonly [graphQLSelectionData]: TData
  readonly result?: GraphQLResultMapping
}

/** Converts between a query's server response and its client data. */
export interface GraphQLResultMapping {
  decode(value: unknown): unknown
  encode(value: unknown): unknown
}

export type AnyGraphQLSelection = GraphQLSelection<unknown>

export type GraphQLSelectionData<TSelection extends AnyGraphQLSelection> = TSelection[typeof graphQLSelectionData]

/**
 * GraphQL source with compile-time result and variable types.
 * Runtime representation intentionally remains a plain string.
 */
export type GraphQLDocument<TData, TVariables extends object = NoGraphQLVariables> = string & {
  readonly [graphQLDocumentTypes]: {
    readonly data: TData
    readonly variables: TVariables
  }
}

export type AnyGraphQLDocument = GraphQLDocument<unknown, object>

export type GraphQLDocumentData<TDocument extends AnyGraphQLDocument> = TDocument[typeof graphQLDocumentTypes]["data"]

export type GraphQLDocumentVariables<TDocument extends AnyGraphQLDocument> =
  TDocument[typeof graphQLDocumentTypes]["variables"]

export function graphqlSelection<TData = unknown>(
  strings: TemplateStringsArray,
  ...selections: readonly AnyGraphQLSelection[]
): GraphQLSelection<TData> {
  return createGraphQLSelection<TData>(composeTemplate(strings, selections))
}

export function composeGraphQLSelections(...selections: readonly AnyGraphQLSelection[]): GraphQLSelection<unknown> {
  assertGraphQLSelections(selections)
  return createGraphQLSelection(selections.map((selection) => selection.source).join("\n"))
}

/** Whether a value was created by the framework's GraphQL selection functions. */
export function isGraphQLSelection(value: unknown): value is AnyGraphQLSelection {
  return value !== null && typeof value === "object" && (value as AnyGraphQLSelection)[graphQLSelectionMarker] === true
}

export function graphql<TData, TVariables extends object = NoGraphQLVariables>(
  strings: TemplateStringsArray,
  ...selections: readonly AnyGraphQLSelection[]
): GraphQLDocument<TData, TVariables> {
  return composeTemplate(strings, selections) as GraphQLDocument<TData, TVariables>
}

function composeTemplate(strings: TemplateStringsArray, selections: readonly AnyGraphQLSelection[]) {
  assertGraphQLSelections(selections)
  let source = strings[0] ?? ""
  for (let index = 0; index < selections.length; index += 1) {
    source += selections[index]!.source
    source += strings[index + 1] ?? ""
  }
  return source
}

export function createGraphQLSelection<TData>(source: string, result?: GraphQLResultMapping): GraphQLSelection<TData> {
  return Object.freeze({
    source,
    [graphQLSelectionMarker]: true,
    ...(result === undefined ? {} : { result }),
  }) as GraphQLSelection<TData>
}

function assertGraphQLSelections(selections: readonly AnyGraphQLSelection[]) {
  for (const selection of selections) {
    if (!isGraphQLSelection(selection) || typeof selection.source !== "string") {
      throw new Error(
        "GraphQL interpolation only accepts values created by graphqlSelection or composeGraphQLSelections."
      )
    }
  }
}
